import { useAuth } from "@clerk/clerk-react";
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { BentoCategory } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { trackEvent } from "utils/tracking";

import { Template } from "../ContactList/schema";
import { OrganizationBulkSendSettings } from "../Discover/Results/DiscoverFilter/AutomationVA/UpcomingPitchScheduleDialog/schema";
import VAPreviewPage from "./PreviewPage";
import VASettings from "./VASettings";

export default function VA() {
  // Contexts

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();

  // States
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [bulkSendSetting, setBulkSendSetting] =
    useState<OrganizationBulkSendSettings | null>(null);
  const [fallbackTemplates, setFallbackTemplates] = useState<Template[]>([]);
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);

  const getCategoriesToPickFrom = async () => {
    if (!currentOrg?.isVaUser) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const fetchSettings = async () => {
    if (!currentOrg?.isVaUser) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings`,
        "GET",
      );
      if (res.bulkSendSetting) {
        setBulkSendSetting(res.bulkSendSetting);
        setFallbackTemplates(res.bulkSendSetting?.templates || []);
      }
      return res.templates;
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const loadData = async () => {
    if (!currentOrg?.isVaUser) return;
    setFetchLoading(true);
    try {
      await Promise.all([fetchSettings(), getCategoriesToPickFrom()]);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (currentOrg?.isVaUser) {
      loadData();
    } else {
      setFetchLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !currentOrg?.id ||
      !currentOrg?.isVaUser ||
      !isPage(routes.virtualAssistant) ||
      isMobileScreen
    )
      return;

    // If we already init FreshChat, we just need to register the user name and email.
    if ((window as any).fcWidget) {
      const fcWidget = (window as any).fcWidget;
      registerUserNameAndEmail(fcWidget);
      return;
    }

    const script = document.createElement("script");
    script.src = "//in.fw-cdn.com/32587301/1436123.js";
    script.async = true;
    script.setAttribute("chat", "true");

    document.body.appendChild(script);
    const fcWidget = (window as any).fcWidget;
    registerUserNameAndEmail(fcWidget);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const registerUserNameAndEmail = (fcWidget: any) => {
    if (!fcWidget) return;
    fcWidget.setExternalId(currentOrg?.id);
    fcWidget.user.setFirstName(currentUser?.name);
    fcWidget.user.setEmail(currentUser?.email);
  };

  if (fetchLoading) {
    return (
      <Box
        sx={{
          color: "#fff",
          zIndex: 9999,
          left: "50%",
          top: "50%",
          ml: "-20px",
          mt: "-20px",
          position: "absolute",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (currentOrg?.isVaUser && bulkSendSetting) {
    return (
      <VASettings
        setBulkSendSetting={setBulkSendSetting}
        bulkSendSetting={bulkSendSetting}
        fallbackTemplates={fallbackTemplates}
        setFallbackTemplates={setFallbackTemplates}
        categoriesToPickFrom={categoriesToPickFrom}
      />
    );
  }

  return <VAPreviewPage />;
}
